/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import InputAdornment from "@material-ui/core/InputAdornment";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import DescriptionIcon from "@material-ui/icons/Description";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import { isArray } from "lodash";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import MetricsStrip from "../../components/MetricsStrip";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];

    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const campaignIndex = state.findIndex((currentCampaign) => currentCampaign.id === campaign.id);

        if (campaignIndex !== -1) {
          state[campaignIndex] = campaign;
        } else {
          newCampaigns.push(campaign);
        }
      });
    }

    return [...state, ...newCampaigns];
  }

  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex((currentCampaign) => currentCampaign.id === campaign.id);

    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    }

    return [campaign, ...state];
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;
    const campaignIndex = state.findIndex((currentCampaign) => currentCampaign.id === campaignId);

    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  tableSearch: {
    width: 320,
    maxWidth: "100%",
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    maxHeight: "68vh",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeadCell: {
    fontWeight: 600,
  },
  actionsCell: {
    whiteSpace: "nowrap",
  },
  helperText: {
    opacity: 0.72,
    marginTop: theme.spacing(0.5),
  },
  loadMoreBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const getStatusMeta = (status) => {
  switch (status) {
    case "EM_ANDAMENTO":
      return {
        label: "Em andamento",
        backgroundColor: "#dbeafe",
        color: "#1d4ed8",
      };
    case "PROGRAMADA":
      return {
        label: "Programada",
        backgroundColor: "#fef3c7",
        color: "#92400e",
      };
    case "FINALIZADA":
      return {
        label: "Finalizada",
        backgroundColor: "#dcfce7",
        color: "#166534",
      };
    case "CANCELADA":
      return {
        label: "Cancelada",
        backgroundColor: "#fee2e2",
        color: "#b91c1c",
      };
    default:
      return {
        label: "Inativa",
        backgroundColor: "#e5e7eb",
        color: "#374151",
      };
  }
};

const Campaigns = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [reloadFlag, setReloadFlag] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [campaigns, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);

      if (!planConfigs.plan.useCampaigns) {
        toast.error("Esta empresa nao possui permissao para acessar essa pagina. Redirecionando.");
        setTimeout(() => {
          history.push("/");
        }, 1000);
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCampaigns();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, reloadFlag, searchParam]);

  useEffect(() => {
    const companyId = user.companyId;

    const onCompanyCampaign = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
      }
    };

    socket.on(`company-${companyId}-campaign`, onCompanyCampaign);

    return () => {
      socket.off(`company-${companyId}-campaign`, onCompanyCampaign);
    };
  }, [socket, user]);

  const fetchCampaigns = async () => {
    try {
      if (pageNumber === 1) {
        dispatch({ type: "RESET" });
      }

      const { data } = await api.get("/campaigns/", {
        params: { searchParam, pageNumber },
      });

      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records || [] });
      setHasMore(Boolean(data.hasMore));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const refreshList = () => {
    setPageNumber(1);
    setReloadFlag((current) => current + 1);
  };

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
    setPageNumber(1);
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }

    setDeletingCampaign(null);
    setConfirmModalOpen(false);
    refreshList();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (event) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      refreshList();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      refreshList();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const summary = useMemo(() => {
    const scheduled = campaigns.filter((campaign) => campaign.status === "PROGRAMADA").length;
    const running = campaigns.filter((campaign) => campaign.status === "EM_ANDAMENTO").length;
    const completed = campaigns.filter((campaign) => campaign.status === "FINALIZADA").length;

    return {
      total: campaigns.length,
      scheduled,
      running,
      completed,
    };
  }, [campaigns]);

  const summaryCards = useMemo(
    () => [
      { key: "campaigns", label: "Campanhas", value: summary.total },
      { key: "scheduled", label: "Programadas", value: summary.scheduled },
      { key: "running", label: "Em andamento", value: summary.running },
      { key: "completed", label: "Finalizadas", value: summary.completed },
    ],
    [summary]
  );

  return (
    <MainContainer className={classes.mainContainer} fullWidth>
      <ConfirmationModal
        title={
          deletingCampaign &&
          `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${deletingCampaign.name}?`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      {campaignModalOpen && (
        <CampaignModal
          resetPagination={refreshList}
          open={campaignModalOpen}
          onClose={handleCloseCampaignModal}
          aria-labelledby="form-dialog-title"
          campaignId={selectedCampaign && selectedCampaign.id}
        />
      )}

      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <div className={classes.contentStack}>
          <Box className={classes.hero}>
            <Box>
              <Typography variant="h5" className={classes.heroTitle}>
                {i18n.t("campaigns.title")}
              </Typography>
            </Box>
            <Box className={classes.heroActions}>
              <Button variant="outlined" onClick={refreshList} disabled={loading}>
                Atualizar
              </Button>
              <Button variant="contained" color="primary" onClick={handleOpenCampaignModal}>
                {i18n.t("campaigns.buttons.add")}
              </Button>
            </Box>
          </Box>

          <MetricsStrip ariaLabel="Indicadores de campanhas" items={summaryCards} />

          <Paper className={classes.tablePaper} variant="outlined">
            <Box className={classes.tableHeader}>
              <Typography variant="body1" className={classes.tableHeaderTitle}>
                Campanhas operacionais
              </Typography>
              <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
                <TextField
                  className={classes.tableSearch}
                  variant="outlined"
                  size="small"
                  placeholder={i18n.t("campaigns.searchPlaceholder")}
                  type="search"
                  value={searchParam}
                  onChange={handleSearch}
                  inputProps={{ "aria-label": "Pesquisar campanhas" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Chip size="small" label={`${summary.total} carregadas`} />
                <Chip size="small" label={`${summary.running} em andamento`} />
              </Box>
            </Box>

            <TableContainer className={classes.tableContainer} onScroll={handleScroll}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      {i18n.t("campaigns.table.name")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      {i18n.t("campaigns.table.status")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      {i18n.t("campaigns.table.contactList")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      {i18n.t("campaigns.table.whatsapp")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      {i18n.t("campaigns.table.scheduledAt")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      {i18n.t("campaigns.table.completedAt")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      {i18n.t("campaigns.table.confirmation")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      {i18n.t("campaigns.table.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.map((campaign) => {
                    const statusMeta = getStatusMeta(campaign.status);

                    return (
                      <TableRow key={campaign.id} hover>
                        <TableCell align="center">{campaign.name}</TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={statusMeta.label}
                            style={{
                              backgroundColor: statusMeta.backgroundColor,
                              color: statusMeta.color,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {campaign.contactListId ? campaign.contactList.name : "Nao definida"}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.whatsappId ? campaign.whatsapp.name : "Nao definido"}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.scheduledAt
                            ? datetimeToClient(campaign.scheduledAt)
                            : "Sem agendamento"}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.completedAt
                            ? datetimeToClient(campaign.completedAt)
                            : "Nao concluida"}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.confirmation ? "Habilitada" : "Desabilitada"}
                        </TableCell>
                        <TableCell align="center" className={classes.actionsCell}>
                          {campaign.status === "EM_ANDAMENTO" && (
                            <IconButton
                              onClick={() => cancelCampaign(campaign)}
                              title="Parar campanha"
                              size="small"
                            >
                              <PauseCircleOutlineIcon />
                            </IconButton>
                          )}
                          {campaign.status === "CANCELADA" && (
                            <IconButton
                              onClick={() => restartCampaign(campaign)}
                              title="Reiniciar campanha"
                              size="small"
                            >
                              <PlayCircleOutlineIcon />
                            </IconButton>
                          )}
                          <IconButton
                            onClick={() => history.push(`/campaign/${campaign.id}/report`)}
                            size="small"
                          >
                            <DescriptionIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEditCampaign(campaign)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setConfirmModalOpen(true);
                              setDeletingCampaign(campaign);
                            }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {loading && <TableRowSkeleton columns={8} />}
                </TableBody>
              </Table>
            </TableContainer>

            {!loading && campaigns.length === 0 && (
              <Box p={4} textAlign="center">
                <Typography variant="h6">Nenhuma campanha encontrada</Typography>
                <Typography variant="body2" className={classes.helperText}>
                  Ajuste a busca ou cadastre uma nova campanha para continuar.
                </Typography>
              </Box>
            )}

            {!loading && hasMore && campaigns.length > 0 && (
              <div className={classes.loadMoreBox}>
                <Button variant="outlined" color="primary" onClick={loadMore}>
                  Carregar mais campanhas
                </Button>
              </div>
            )}
          </Paper>
        </div>
      )}
    </MainContainer>
  );
};

export default Campaigns;
